<template>
    <LayoutWithContainer>
        <template slot="breadcrumbs" class="breadcrumb">
            <router-link to="/sectors">Sectors</router-link>
        </template>
        <section v-if="sector" class="vsector">
            <header class="vsector__header">
                <div class="vsector__title">
                    <h1 slot="header" v-text="sector.name"/>
                    <router-link :to="`/sectors/${sector.id}/edit`" v-if="can('createMembers')">
                        <span>Edit</span>
                        <SvgIcon icon="edit" :is-filled="true" />
                    </router-link>
                </div>
                <div class="vsector__actions" />
            </header>
            <aside class="vsector__sidebar">
                <SectorCounts :sector-id="selectedId" />
                <div class="vsector__sectors">
                    <span class="subheading">Filter by subsector</span>
                    <SectorViewFilters
                        :default-items="[{ id: sector.id, label: sector.name, children: null }]"
                        :default-sector-id="sector.id"
                        v-model="selectedId"
                        label="Filter posts by..."
                    />
                </div>
                <div class="vsector__leader" v-if="sector.leader">
                    <span class="subheading">Sector Leader</span>
                    <UserCard
                        :user="sector.leader"
                        :borderless="true"
                        :small="true"
                    />
                </div>
                <div class="vsector__table">
                    <h3 class="subheading">Top Response Rate</h3>
                    <SectorLeagueTable :id="sector.id" />
                </div>
            </aside>
            <div class="vsector__feed">
                <span class="subheading" v-text="`Post in the ${sector.name} sector`"></span>
                <CreatePost
                    ref="createPostForm"
                    :sector-id="id"
                    :default-values="{ sectorOptions: [{ id: sector.id, label: sector.name, children: null }] }"
                    @created="handlePostCreated"
                    @form-submit="handlePostFormSubmit"
                />
                <SectorPostFeed
                    ref="feed"
                    :sector-ids="[selectedId]"
                />
            </div>
        </section>
    </LayoutWithContainer>
</template>
<script>
import { mapGetters } from 'vuex';
import API from '@api';
import LayoutWithContainer from '@layouts/LayoutWithContainer';
import LayoutBase from '@layouts/LayoutBase';
import SvgIcon from '@components/SvgIcon';
import SectorViewFilters from '@components/Sector/SingleView/Filters';
import SectorCounts from '@components/Sector/Sidebar/Counts';
import CreatePost from '@components/Sector/SingleView/CreatePost';
import SectorPostFeed from '@components/Sector/Feed/Feed';
import sectors from '../../sectors';
import SearchModalForm from '@components/SearchModal/Form.vue';
import SectorLeagueTable from '@components/Sector/Feed/LeagueTable.vue';
import UserCard from '@components/User/UserCard.vue';
import merge from 'lodash.merge';

export default {
    name: 'SectorSingleView',
    components: {
        UserCard,
        SectorLeagueTable,
        SearchModalForm,
        CreatePost,
        SvgIcon,
        LayoutBase,
        LayoutWithContainer,
        SectorViewFilters,
        SectorPostFeed,
        SectorCounts
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            isLoading: true,
            selectedId: this.id,
            sector: null,
        }
    },
    methods: {
        async getResource() {
            try {
                this.isLoading = true;
                this.sector = await API.getSector(this.id);
            } catch(err) {
                console.log(err);
                console.warn(`[SectorEdit.vue] ${ err.message }`);
            } finally {
                this.isLoading = false;
            }
        },
        async handlePostFormSubmit(formData){
            try {
                formData = merge({},
                    formData,
                    { sectors: [...formData.sectors, this.id] }
                )
                this.$refs.createPostForm.startWait();

                const data = await API.saveSectorPost(formData);

                this.handlePostCreated(data);
                this.$refs.createPostForm.resetForm();
            } catch(err) {
                console.warn(err.message);
                this.$refs.createPostForm.updateErrors(err);
            } finally {
                this.$refs.createPostForm.endWait();
            }
        },
        handlePostCreated(post) {
            this.$refs.feed.loadInitial(false, post);
        }
    },
    created() {
        this.getResource();
    },
    computed: {
        ...mapGetters(['can']),
    },
}
</script>
<style lang="scss" scoped>
@import '~@/theme/_variables.scss';
.breadcrumb {
    margin-bottom: .7rem;
}

.subheading {
    color: #232D37;
    display: block;
    font-size: 1.8rem;
    line-height: 2.3rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
}

.count {
    margin: 0;

    dt {
        color: #657787;
        font-size: 1.5rem;
        letter-spacing: 0.054rem;
        line-height: 1.8rem;
        margin-bottom: .4rem;
    }

    dd {
        color: $link;
        font-size: 2rem;
        line-height: 2.4rem;
        font-weight: 500;
        margin-left: 0;
    }
}

.counts {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;

    .counts__item {
        flex: 0 0 calc(50% - 1rem);
    }
}

.vsector {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 3rem;

    .vsector__header {
        flex: 1 0 100%;
        display: flex;
        justify-content: space-between;

        .vsector__title {
            display: flex;
            align-items: flex-start;
            column-gap: 1rem;

            h1 {
                margin-bottom: 0;
            }

            a {
                display: flex;
                align-items: center;
                column-gap: .5rem;
                position: relative;
                top: .3rem;
                color: $link;

                svg {
                    height: 1.2rem;
                    width: 1.2rem;
                    position: relative;
                    top: -.1rem;
                }
            }
        }
    }

    .vsector__sidebar {
        flex: 0 0 26.5rem;
        max-width: 26.5rem;
        white-space: wrap;

        > *:first-child ~ * {
            margin-top: 2rem;
            border-top: .1rem solid #CFCFCF;
            padding-top: 2rem;
        }
    }

    .vsector__feed {
        flex: 1 0 0;
        white-space: wrap;
    }

    @media screen and (max-width: 1200px) {
        .vsector__header {
            order: 1;
        }

        .vsector__sidebar {
            order: 3;
            max-width: 100%;
            flex: 0 0 100%;
        }

        .vsector__feed {
            order: 2;
            flex: 0 0 100%;
        }
    }
}
</style>

